// ============================================
// components/gunny/GunnyAllotModal.tsx
// Splits a PO into per-society gunny_utilization rows. Two modes:
//   • Single — pick one society + bags
//   • Bulk   — table of all active PACS in the PO's district, bags per row,
//             submitted in a single atomic request.
// ============================================
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Send } from 'lucide-react';
import { pacsAPI, commoditiesAPI, gunnyPosAPI, godownsAPI, reportsAPI } from '../../api/services';
import type { PACSEntity } from '../../types/markfed';

interface Commodity { id: number; name: string; is_active: boolean; }
interface Godown {
  id: number;
  godown_name: string;
  godown_type: string | null;
  capacity_mts: number | null;
  is_active: boolean;
}

interface PO {
  id: number;
  season_id: number;
  po_number: string;
  total_bags: number;
  delivery_district_id: number;
}

interface Props {
  po: PO | null;
  onClose: () => void;
  onAllotted?: () => void;
}

type Mode = 'single' | 'bulk';

interface PoSummary {
  po_total_bags: number;
  previously_allotted: number;
  just_allotted: number;
  remaining: number;
}

export const GunnyAllotModal: React.FC<Props> = ({ po, onClose, onAllotted }) => {
  const [mode, setMode] = useState<Mode>('single');
  const [pacsList, setPacsList] = useState<PACSEntity[]>([]);
  const [godowns, setGodowns] = useState<Godown[]>([]);
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [available, setAvailable] = useState<number | null>(null);
  const [godownAvail, setGodownAvail] = useState<Map<number, number>>(new Map());
  const [commodityId, setCommodityId] = useState<number>(0);
  const [godownId, setGodownId] = useState<number>(0);

  // Single-mode state
  const [pacsId, setPacsId] = useState<number>(0);
  const [bags, setBags] = useState<number>(0);

  // Bulk-mode state: pacs_id -> bags
  const [bulk, setBulk] = useState<Record<number, number>>({});

  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<PoSummary | null>(null);

  const open = !!po;

  useEffect(() => {
    if (!open || !po) return;
    let alive = true;
    setErr(null);
    setSummary(null);
    setBags(0);
    setPacsId(0);
    setBulk({});
    setCommodityId(0);
    setGodownId(0);
    setAvailable(null);
    setMode('single');
    (async () => {
      try {
        const [pacsRes, comRes, remRes, gdRes, recRes] = await Promise.all([
          pacsAPI.list(po.delivery_district_id),
          commoditiesAPI.list(),
          gunnyPosAPI.remaining(po.season_id, { district_id: po.delivery_district_id }),
          godownsAPI.list(po.delivery_district_id),
          reportsAPI.gunnyReconciliation({
            season_id: po.season_id, district_id: po.delivery_district_id,
          }),
        ]);
        if (!alive) return;
        setPacsList((pacsRes.data as PACSEntity[]).filter((p) => p.is_active));
        setCommodities((comRes.data as Commodity[]).filter((c) => c.is_active));
        setGodowns((gdRes.data as Godown[]).filter((g) => g.is_active));
        const remainingAll = Array.isArray(remRes.data) ? remRes.data : [];
        const mine = remainingAll.find((r: any) => r.po_bags === po.total_bags);
        setAvailable(mine ? mine.remaining : po.total_bags);
        // Per-godown available from the reconciliation view (current on_hand)
        const map = new Map<number, number>();
        for (const r of recRes.data.rows) {
          if (!r.is_pool && r.godown_id != null && r.on_hand != null) {
            map.set(r.godown_id, r.on_hand);
          }
        }
        setGodownAvail(map);
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Failed to load societies / commodities / godowns');
      }
    })();
    return () => { alive = false; };
  }, [open, po]);

  const bulkTotal = useMemo(
    () => Object.values(bulk).reduce((s, n) => s + (Number(n) || 0), 0),
    [bulk],
  );

  const disableSubmit = useMemo(() => {
    if (saving || !commodityId || available == null) return true;
    if (mode === 'single') {
      return !pacsId || bags <= 0 || bags > available;
    }
    return bulkTotal <= 0 || bulkTotal > available;
  }, [saving, commodityId, available, mode, pacsId, bags, bulkTotal]);

  if (!open || !po) return null;

  const submitSingle = async () => {
    if (!po) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await gunnyPosAPI.allot(po.season_id, po.id, {
        pacs_id: pacsId,
        commodity_id: commodityId,
        bags,
        godown_id: godownId || undefined,
      });
      setSummary(res.data.po_summary);
      setAvailable(res.data.po_summary.remaining);
      setBags(0);
      setPacsId(0);
      if (onAllotted) onAllotted();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Allotment failed');
    } finally {
      setSaving(false);
    }
  };

  const submitBulk = async () => {
    if (!po) return;
    const allocations = Object.entries(bulk)
      .map(([pid, b]) => ({ pacs_id: Number(pid), bags: Number(b) || 0 }))
      .filter((a) => a.bags > 0);
    if (allocations.length === 0) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await gunnyPosAPI.bulkAllot(po.season_id, po.id, {
        commodity_id: commodityId,
        godown_id: godownId || undefined,
        allocations,
      });
      setSummary(res.data.po_summary);
      setAvailable(res.data.po_summary.remaining);
      setBulk({});
      if (onAllotted) onAllotted();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Bulk allotment failed');
    } finally {
      setSaving(false);
    }
  };

  const submit = () => (mode === 'single' ? submitSingle() : submitBulk());

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Allot bags from PO {po.po_number}</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Creates utilization rows (status&nbsp;<em>draft</em>) for each society. The pipeline picks up from there.
              </p>
            </div>
            <div className="flex gap-1 bg-gray-100 rounded p-1 text-xs">
              <button
                onClick={() => setMode('single')}
                className={`px-3 py-1 rounded ${mode === 'single' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              >Single</button>
              <button
                onClick={() => setMode('bulk')}
                className={`px-3 py-1 rounded ${mode === 'bulk' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              >Bulk</button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-amber-50 border-b grid grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-500">PO total</p>
            <p className="font-semibold tabular-nums">{po.total_bags.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Available</p>
            <p className="font-semibold tabular-nums text-amber-800">
              {available == null ? '…' : available.toLocaleString()}
            </p>
          </div>
          {mode === 'bulk' ? (
            <>
              <div>
                <p className="text-xs text-gray-500">This batch</p>
                <p className="font-semibold tabular-nums">{bulkTotal.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Remaining after</p>
                <p className={`font-semibold tabular-nums ${available != null && bulkTotal > available ? 'text-red-700' : ''}`}>
                  {available == null ? '…' : Math.max(available - bulkTotal, 0).toLocaleString()}
                </p>
              </div>
            </>
          ) : summary ? (
            <>
              <div>
                <p className="text-xs text-gray-500">Just allotted</p>
                <p className="font-semibold text-emerald-700 tabular-nums">{summary.just_allotted.toLocaleString()}</p>
              </div>
              <div />
            </>
          ) : (
            <><div /><div /></>
          )}
        </div>

        {err && (
          <div className="mx-6 mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" /> {err}
          </div>
        )}

        <div className="px-6 py-5">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commodity</label>
              <select
                value={commodityId}
                onChange={(e) => setCommodityId(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm bg-white"
              >
                <option value={0}>Select commodity…</option>
                {commodities.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Godown <span className="text-gray-400 font-normal">(optional — physical storage location)</span>
              </label>
              <select
                value={godownId}
                onChange={(e) => setGodownId(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm bg-white"
              >
                <option value={0}>Unassigned (don't check godown balance)</option>
                {godowns.map((g) => {
                  const avail = godownAvail.get(g.id);
                  const availLabel =
                    avail == null ? 'no delivery yet' : `${avail.toLocaleString()} bags on-hand`;
                  return (
                    <option key={g.id} value={g.id} disabled={avail === 0}>
                      {g.godown_name}{g.godown_type ? ` (${g.godown_type.toUpperCase()})` : ''}
                      {` · ${availLabel}`}
                    </option>
                  );
                })}
              </select>
              {godownId > 0 && godownAvail.get(godownId) != null && (
                <p className="text-xs text-gray-500 mt-1">
                  {godownAvail.get(godownId)!.toLocaleString()} bags currently at this godown.
                </p>
              )}
            </div>
          </div>

          {mode === 'single' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Society (PACS)</label>
                <select value={pacsId} onChange={(e) => setPacsId(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded text-sm bg-white">
                  <option value={0}>Select a society…</option>
                  {pacsList.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bags</label>
                <input
                  type="number"
                  min={0}
                  max={available ?? undefined}
                  value={bags || ''}
                  onChange={(e) => setBags(Number(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded text-sm"
                  placeholder={available != null ? `Up to ${available}` : ''}
                />
                {available != null && bags > available && (
                  <p className="text-xs text-red-600 mt-1">Exceeds available ({available.toLocaleString()}).</p>
                )}
              </div>
            </div>
          ) : (
            <BulkTable
              pacs={pacsList}
              bulk={bulk}
              setBulk={setBulk}
              available={available}
              bulkTotal={bulkTotal}
            />
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
            Close
          </button>
          <button
            onClick={submit}
            disabled={disableSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {saving
              ? (mode === 'bulk' ? 'Allotting…' : 'Allotting…')
              : mode === 'bulk' ? `Allot ${bulkTotal.toLocaleString()} bags to ${Object.values(bulk).filter((b) => Number(b) > 0).length} societies` : 'Allot'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BulkTable: React.FC<{
  pacs: PACSEntity[];
  bulk: Record<number, number>;
  setBulk: (b: Record<number, number>) => void;
  available: number | null;
  bulkTotal: number;
}> = ({ pacs, bulk, setBulk, available, bulkTotal }) => {
  if (pacs.length === 0) {
    return <p className="text-sm text-gray-500">No active societies found in this district.</p>;
  }
  const update = (id: number, v: string) => {
    const n = Number(v) || 0;
    const next = { ...bulk };
    if (n <= 0) delete next[id];
    else next[id] = n;
    setBulk(next);
  };
  const over = available != null && bulkTotal > available;

  return (
    <div className="border rounded max-h-[320px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr className="text-left text-xs uppercase text-gray-600">
            <th className="px-3 py-2">Society (PACS)</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2 text-right">Bags</th>
          </tr>
        </thead>
        <tbody>
          {pacs.map((p) => (
            <tr key={p.id} className="border-t">
              <td className="px-3 py-1.5">{p.name}</td>
              <td className="px-3 py-1.5 text-xs text-gray-600">{p.type}</td>
              <td className="px-3 py-1.5 text-right">
                <input
                  type="number"
                  min={0}
                  value={bulk[p.id] || ''}
                  onChange={(e) => update(p.id, e.target.value)}
                  className="w-28 px-2 py-1 border rounded text-sm text-right tabular-nums"
                  placeholder="0"
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className={`border-t bg-gray-50 sticky bottom-0 ${over ? 'text-red-700' : ''}`}>
          <tr>
            <td colSpan={2} className="px-3 py-2 text-sm font-semibold">Total</td>
            <td className="px-3 py-2 text-right tabular-nums font-semibold">
              {bulkTotal.toLocaleString()}{available != null ? ` / ${available.toLocaleString()}` : ''}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default GunnyAllotModal;
